/**
 * Full-extent vector render of the Map/Remap scene for PDF export (WI #5531).
 *
 * Drives the SAME scene painters that paint the on-screen canvas — there is no
 * second renderer to maintain. The difference is only the target and the framing:
 *
 *   - Target: an `svgcanvas`-backed `ExportContext` instead of the layered
 *     `<canvas>` 2D contexts, so the output is a single vector SVG (selectable
 *     text, crisp at any zoom).
 *   - Framing: the scene is sized to the WHOLE map (`START_X + scaleX(mapLength)`
 *     wide, `map.height` tall) rather than the viewport, so nothing is culled.
 *     The caller sets `offset = 0` + `isExportingPDF = true` first (mirroring the
 *     legacy SVG export), so `screenX` lays the whole timeline out from the left.
 *
 * Only the static, fully-rendered scene is drawn: background (bands, past-time,
 * timeline, phases, borders), content (milestones, links, hexagons, banners) and
 * the workstream sidebar. Transient/interaction-only layers — move limits,
 * long-press ring, hover tooltips, the minimap, remote-user presence and every
 * tool ghost — are intentionally skipped (export is the resting state of the map).
 */
import { useAiLiveStore } from '@/store/aiLiveStore';
import { useIoStore } from '@/store/ioStore';
import { usePresenceStore } from '@/store/presenceStore';
import { useProfileStore } from '@/store/profileStore';
import { useProjectStore } from '@/store/projectStore';
import { START_X, useMapStore } from '@/store/remapStore';
import { useUiStore } from '@/store/uiStore';
import { useUserStore } from '@/store/userStore';
import { TextMeasurer } from '../draw/text';
import { setGroupOpacityExportMode } from '../engine/groupOpacity';
import { HitRegistry } from '../engine/HitRegistry';
import { ImageCache } from '../engine/ImageCache';
import { SceneState } from '../engine/SceneState';
import { activityLayout } from '../layout/activityLayout';
import { drawBands } from '../paint/background/BandsPainter';
import { drawPastTime } from '../paint/background/PastTimePainter';
import { drawPhases } from '../paint/background/PhasesPainter';
import { drawSelectedPhaseBackground } from '../paint/background/SelectedPhaseBackgroundPainter';
import {
  drawTemplateTimeline,
  drawTimeline,
} from '../paint/background/TimelinePainter';
import { drawWorkstreamBorders } from '../paint/background/WorkstreamBordersPainter';
import { drawBanners } from '../paint/content/BannerPainter';
import { computeContentFrame } from '../paint/content/contentFrame';
import { drawGroupMarkers } from '../paint/content/GroupMarkersPainter';
import { drawHexagons } from '../paint/content/HexagonsPainter';
import { drawLabels } from '../paint/content/LabelsPainter';
import { drawLinks } from '../paint/content/LinksPainter';
import { drawMilestoneLabels } from '../paint/content/MilestoneLabelsPainter';
import { drawMilestoneLines } from '../paint/content/MilestoneLinesPainter';
import { drawMilestoneMarkers } from '../paint/content/MilestoneMarkersPainter';
import { drawRequiredLines } from '../paint/content/RequiredLinesPainter';
import { drawSidebar } from '../paint/overlay/SidebarPainter';
import { createExportContext } from './ExportContext';
import type { ExportContext } from './ExportContext';
import type { SceneContext } from '../engine/SceneContext';

/** Right-hand padding so banners/labels that overhang the last phase aren't clipped. */
const RIGHT_PADDING = 80;

export interface MapSvgExport {
  svg: string;
  width: number;
  height: number;
}

/**
 * Render the current map to a standalone SVG string. Assumes the caller has put
 * the store into export framing (`offset = 0`, `isExportingPDF = true`) and will
 * restore it afterwards.
 */
export function renderMapToSvg(): MapSvgExport {
  const map = useMapStore();
  const io = useIoStore();

  // Crop to the VISIBLE phase span, not the full timeframe: hidden phases (leading
  // or trailing) otherwise leave an empty stretch of timeline with no content and
  // no phase ribbon above. `firstVisiblePhase.start` / `lastVisiblePhase.end` are 0
  // / `mapLength` when nothing is hidden, so this is a no-op for the common case.
  const startPos = io.firstVisiblePhase?.start ?? 0;
  const endPos = io.lastVisiblePhase?.end ?? map.mapLength;
  const leftCrop = map.scaleX(startPos);

  const width =
    Math.ceil(START_X + map.scaleX(endPos) - leftCrop) + RIGHT_PADDING;
  const height = Math.ceil(map.height);

  const exportCtx = createExportContext(width, height);
  const { ctx } = exportCtx;

  const scene: SceneContext = {
    width,
    height,
    dpr: 1,
    exporting: true,
    // Shift the whole timeline left so the first visible phase starts at START_X;
    // content/timeline outside [startPos, endPos] then falls outside the canvas and
    // is culled by the painters.
    screenX: (pos: number): number => START_X - leftCrop + map.scaleX(pos),
    map,
    io,
    project: useProjectStore(),
    user: useUserStore(),
    ui: useUiStore(),
    presence: usePresenceStore(),
    profile: useProfileStore(),
    // The AI layer is screen-only; the export never paints it.
    aiLive: useAiLiveStore(),
    text: new TextMeasurer(),
    // Best-effort: images decode asynchronously, so any not already cached in this
    // fresh instance simply won't appear in the export. The static scene is the
    // requirement; avatars/header image are a nice-to-have.
    images: new ImageCache(() => undefined),
    state: new SceneState(),
    hits: new HitRegistry(),
    content: null,
    layout: null,
  };
  // Settled (non-animated) activity layout on the export geometry; computed
  // fresh here so the live engine's animation state is left untouched.
  scene.layout = activityLayout.staticView(ctx, scene);

  setGroupOpacityExportMode(true);
  try {
    paintBackground(exportCtx, scene);
    paintContent(exportCtx, scene);
    // Sidebar is the only "chrome" that belongs in a static export.
    safe(exportCtx, () => drawSidebar(ctx, scene));
  } finally {
    setGroupOpacityExportMode(false);
  }

  return { svg: exportCtx.toSvg(), width, height };
}

/** Background layer, mirroring SceneRenderer.paintBackground (no viewport clear). */
function paintBackground(exp: ExportContext, scene: SceneContext): void {
  const { ctx } = exp;
  safe(exp, () => drawBands(ctx, scene));

  if (scene.map.isTemplate) {
    safe(exp, () => drawTemplateTimeline(ctx, scene));
    safe(exp, () => drawSelectedPhaseBackground(ctx, scene));
    safe(exp, () => drawPhases(ctx, scene));
    safe(exp, () => drawWorkstreamBorders(ctx, scene));

    return;
  }

  safe(exp, () => drawPastTime(ctx, scene));
  safe(exp, () => drawTimeline(ctx, scene));
  safe(exp, () => drawSelectedPhaseBackground(ctx, scene));
  safe(exp, () => drawPhases(ctx, scene));
  safe(exp, () => drawWorkstreamBorders(ctx, scene));
}

/** Content layer, mirroring SceneRenderer.paintContent minus the transient bits. */
function paintContent(exp: ExportContext, scene: SceneContext): void {
  const { ctx } = exp;
  scene.content = computeContentFrame(ctx, scene);

  safe(exp, () => drawMilestoneLines(ctx, scene));
  safe(exp, () => drawMilestoneMarkers(ctx, scene));
  safe(exp, () => drawMilestoneLabels(ctx, scene));
  safe(exp, () => drawRequiredLines(ctx, scene));
  safe(exp, () => drawLabels(ctx, scene));
  safe(exp, () => drawLinks(ctx, scene));
  safe(exp, () => drawHexagons(ctx, scene));
  safe(exp, () => drawGroupMarkers(ctx, scene));
  safe(exp, () => drawBanners(ctx, scene));
}

/**
 * A failing painter (e.g. a missing image) must not blank the whole export — nor
 * shift the rest of it: dying mid-`save()` leaves that painter's `translate` on
 * the shared context, so always unwind back to the base state afterwards.
 */
function safe(exp: ExportContext, paint: () => void): void {
  try {
    paint();
  } catch (error) {
    console.error('[canvas export] painter failed', error);
  } finally {
    exp.resetState();
  }
}
